// ===== Imports =====
import React, {useCallback, useEffect, useRef, useState} from 'react';
import classNames from "classnames";
import {useFormikContext} from 'formik';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {Launch} from "@material-ui/icons";
import axios from 'axios';

import {ReduxState} from "../../../store/types";
import {actions} from "../../../store";
import {Colors} from "../../../styles/Colors";
import {AiFormInterface, AiFormType, useStyles} from "./AiForm.utils";
import {Agent} from "../../../store/types/AiAgent";
import Config from '../../../config.json';

import SwitchWithLabel from "../../SwitchWithLabel/SwitchWithLabel";
import {AiIcon} from "../../Extensions/AiIcon";
import AsyncSelectField from "../../AsyncSelectField/AsyncSelectField";
import Button from "../../Button/Button";
import Loader from "../../Loader/Loader";

// ===== Component =====
const AiForm: React.FC<AiFormInterface> = (
    {
        emailIsPresent,
        countryIsPresent,
        tenantId,
        isDataLoading,
        onAiEnabledChange,
        handleDirtyChange,
        handleSetSubmitFunc,
        handleSetIsValidFunc,
        handleSubmitForm
    }) => {
    // ===== Hooks & Context =====
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {values, setFieldValue, dirty, handleSubmit, isValid, errors} = useFormikContext<AiFormType>();

    // ===== Store Selectors =====
    const sign_in_link = useSelector((state: ReduxState) => state.aiAgent.sign_in_link);
    const token = useSelector((state: ReduxState) => state.aiAgent.token);

    // Guard to avoid repeated tenant check dispatches on rapid toggles
    const hasRequestedTenantRef = useRef(false);

    const [dropdownOpenKey, setDropdownOpenKey] = useState(0);
    const onMenuOpen = useCallback(() => setDropdownOpenKey(k => k + 1), []);

    // ===== Memoized Handlers =====
    const localSubmitHandler = React.useCallback(() => {
        handleSubmit();
    }, [handleSubmit]);

    const setAiEnabled = useCallback((val: boolean) => {
        setFieldValue('aiEnabled', val);
    }, [setFieldValue]);

    const onChangeList = useCallback((value: any) => {
        if (value?.agent_id !== 'unknown') {
            const { label, ...agent } = value ?? {};
            setFieldValue('aiAgent', value ? agent : null);
        }
    }, [setFieldValue]);

    const onPortalClick = useCallback(() => {
        window.open(sign_in_link);
    }, [sign_in_link]);

    const isOptionSelected = useCallback(
        (option: any) => option?.name === values.aiAgent?.name,
        [values.aiAgent]
    );
    const getOptionDisabled = useCallback((o: any) => o?.agent_id === 'unknown', []);

    const loadOptions = useCallback(async (search: string) => {
        if (!token?.access_token) {
            return { options: [], hasMore: false };
        }
        try {
            const response = await axios.get(
                Config.VOICE_AI_ADAPTER_URL + '/voice-ai-agents',
                {
                    headers: {
                        accept: 'application/json',
                        Authorization: `Bearer ${token.access_token}`,
                    },
                }
            );
            dispatch(actions.getAiAgentsList.success(response.data));
            const agents: Agent[] = search
                ? response.data.agents.filter((a: Agent) =>
                    a.name.toLowerCase().includes(search.toLowerCase()))
                : response.data.agents;
            return {
                options: agents.map(a => ({ ...a, label: a.name })),
                hasMore: false,
            };
        } catch (e: any) {
            dispatch(actions.getAiAgentsList.failure({
                error: e?.response?.data?.details || 'unknown'
            }));
            return { options: [], hasMore: false };
        }
    }, [token, dispatch]);

    // ===== Effects =====
    useEffect(() => {
        handleDirtyChange?.('ai', dirty);
    }, [dirty]);
    useEffect(() => {
        handleSetSubmitFunc?.('ai', localSubmitHandler);
    }, [handleSetSubmitFunc, localSubmitHandler]);
    useEffect(() => {
        handleSetIsValidFunc?.('ai', () => isValid);
    }, [isValid]);

    useEffect(() => {
        if (values.aiEnabled && !tenantId && !hasRequestedTenantRef.current && !isDataLoading) {
            const internalAdapterToken = localStorage.getItem('internal_adapter_token');
            if (internalAdapterToken) {
                hasRequestedTenantRef.current = true;
                dispatch(actions.checkOrCreateTenant.request({createIfNotExists: true}));
            }
        }
        // Reset guard when disabling AI or tenantId becomes available
        if (!values.aiEnabled || tenantId) {
            hasRequestedTenantRef.current = false;
        }
    }, [values.aiEnabled, tenantId, isDataLoading, dispatch]);

    // ===== Render =====
    return (
        <div className={classes.root} data-testid={'ai-form'}>
            <div className={classes.container}>
                <SwitchWithLabel
                    id="aiEnabled"
                    field="aiEnabled"
                    label={t('screens:extensions.aiAgent', 'AI agent')}
                    value={values.aiEnabled}
                    disabled={!emailIsPresent || !countryIsPresent || isDataLoading}
                    setValue={() => {
                        const newVal = !values.aiEnabled;
                        setAiEnabled(newVal);
                        onAiEnabledChange?.(newVal);
                    }}
                    icon={
                        <AiIcon dataQa={'ai-toggle-icon'} title={'AI agent'} />
                    }
                    className={classes.swicher}
                    dataQa={'ai-enabled-component'}
                    customDisabledTooltip={ !emailIsPresent ? t(
                        'tooltips:extensions.reqEmailToEnable',
                    ) : !countryIsPresent ? t('tooltips:extensions.reqCountryToEnable') : undefined }
                />

                {isDataLoading && (
                    <div className={classes.loader}>
                        <Loader dataQa="ai-form-loader" dataTestId="ai-form-loader" />
                    </div>
                )}

                {values.aiEnabled && !isDataLoading && (
                    <div>
                        <div className={classes.rowContainer}>
                            <AsyncSelectField
                                title={t('screens:extensions.aiAgent')}
                                loadOptions={loadOptions}
                                value={values.aiAgent ? { ...values.aiAgent, label: values.aiAgent.name } : null}
                                onChange={onChangeList}
                                isOptionSelected={isOptionSelected}
                                isOptionDisabled={getOptionDisabled}
                                dataQa="agent-input"
                                disableClearable
                                required
                                helperText={errors.aiAgent as string}
                                className={classes.input}
                                onMenuOpen={onMenuOpen}
                                cacheUniqs={[dropdownOpenKey]}
                            />
                        </div>
                        <div>
                            <Button
                                dataQa="open-ai-configuration-button"
                                primary
                                accent
                                className={classNames(classes.buttonStyle)}
                                disabled={!sign_in_link}
                                onClick={onPortalClick}
                                icon={<Launch htmlColor={Colors.Gray5} />}
                            >
                                {t('screens:extensions.aiAgentAccessLink')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ===== Export =====
export default AiForm;
